const MINUTE = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/;

/** What an empty value shows. */
export const EMPTY = '—';

export function text(value: string | null): string {
    return value || EMPTY;
}

/**
 * An instant as `yyyy-MM-dd HH:mm`, 24-hour, in the browser's time zone, read once when this module loads. The
 * Swedish locale is only borrowed for its ISO-shaped date and 24-hour clock.
 */
const LOCAL_MINUTE = new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});

/**
 * An instant as `yyyy-MM-dd HH:mm` in the viewer's time zone, whatever the tenant's date format: how the insider
 * list shows its access times, how its form holds them and how its Excel export prints them. Empty for none.
 */
export function formatLocalMinute(instant: string | Date | null): string {
    return instant ? LOCAL_MINUTE.format(new Date(instant)) : '';
}

/**
 * The ISO instant a `yyyy-MM-dd HH:mm` entry names in the viewer's time zone; `null` for anything else. The round
 * trip turns away a wall clock that does not exist: a calendar-impossible date that `Date` would roll over, such as
 * February 30th, and a time inside the zone's spring-forward gap. The autumn hour that repeats reads as its first
 * occurrence.
 */
export function parseLocalMinute(value: string): string | null {
    const text = value.trim();
    const parts = MINUTE.exec(text)?.slice(1).map(Number);
    if (!parts) {
        return null;
    }
    const [year, month, day, hour, minute] = parts;
    const date = new Date(year, month - 1, day, hour, minute);
    return formatLocalMinute(date) === text ? date.toISOString() : null;
}

/**
 * The instant to send for a typed access time: the stored one while the minute shown is unchanged, so an
 * edit never truncates the seconds the server stamped; otherwise the typed minute, or `null`.
 */
export function toInstant(typed: string, stored: string | null | undefined): string | null {
    return stored && formatLocalMinute(stored) === typed.trim() ? stored : parseLocalMinute(typed);
}
